import { ReactNode } from "react";

import { TextField, styled } from "@mui/material";

// Collapses the (unused) text input so the field shows only the link when one is provided. It still
// grows into the free space, so any end adornment sits at the end of the field rather than butting
// up against the link.
const StyledField = styled(TextField)({
  "& .MuiInputBase-input": { width: 0, minWidth: 0, padding: 0, flexGrow: 1 },
  "& .MuiInputBase-root": { minHeight: 40 },
});

interface Props {
  label: string;
  value: string;
  link?: ReactNode;
  /** Optional actions rendered at the end of the field (icon buttons etc.). */
  endAdornment?: ReactNode;
  helperText?: ReactNode;
  /** Renders border, label, and helper text in the warning color (pending-removal marking). */
  warning?: boolean;
}

/**
 * Read-only reference field: shows a clickable {@link link} (a navigable entity link) when one is
 * provided, otherwise a disabled text field displaying {@link value}. Backs the read-only reference
 * fields (Kund:in, Aktenvermerk) in the assignment dialogs.
 */
export function ReadOnlyLinkField({ label, value, link, endAdornment, helperText, warning }: Props) {
  if (link) {
    return (
      <StyledField
        fullWidth
        size="small"
        label={label}
        helperText={helperText ?? " "}
        sx={
          warning
            ? {
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" },
                "& .MuiInputLabel-root": { color: "warning.main" },
                "& .MuiFormHelperText-root": { color: "warning.main" },
              }
            : undefined
        }
        slotProps={{
          inputLabel: { shrink: true, sx: { fontSize: "0.875rem" } },
          input: {
            readOnly: true,
            startAdornment: link,
            endAdornment,
          },
        }}
      />
    );
  }

  return <TextField fullWidth disabled size="small" label={label} value={value} helperText=" " />;
}
